import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthProvider";
import { useT } from "@/i18n";
import Logo from "@/components/common/Logo";
import ThemeToggle from "@/components/common/ThemeToggle";
import LanguageToggle from "@/components/common/LanguageToggle";

export default function ResetPassword() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("auth.resetSuccess"));
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex items-center justify-between p-6">
        <Logo />
        <div className="flex items-center gap-2"><LanguageToggle /><ThemeToggle /></div>
      </div>
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("auth.resetTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("auth.resetSubtitle")}</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <input
              data-testid="reset-password" type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.password")}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand"
            />
            <input
              data-testid="reset-confirm" type="password" required minLength={6} value={confirm}
              onChange={(e) => setConfirm(e.target.value)} placeholder={t("auth.confirmPassword")}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand"
            />
            <button
              data-testid="reset-submit" type="submit" disabled={loading}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-primary font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? t("common.loading") : t("auth.resetButton")}
            </button>
          </form>
          <Link to="/login" className="mt-6 inline-block text-sm text-muted-foreground hover:text-foreground">
            {t("auth.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
